import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { actionError } from '@/features/agent-proposals/IntakeReview';
import type { Detail } from '@/features/agent-proposals/ReferralDetail';

export function ProvisionalReview({ detail }: { detail: Detail }) {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const cache = useQueryClient();
  const stage = detail.provisional!;
  const refresh = () => {
    void cache.invalidateQueries({ queryKey: ['agent-detail'] });
    void cache.invalidateQueries({ queryKey: ['assistant-incoming'] });
    void cache.invalidateQueries({ queryKey: ['patient'] });
  };
  const reject = useMutation({
    mutationFn: () =>
      clientApi.post(`/api/v1/organizations/${organization?.organization_id}/assistant/incoming/${detail.id}/reject`, {
        expected_version: stage.version,
      }),
    onSuccess: refresh,
  });
  const confirm = useMutation({
    mutationFn: () =>
      clientApi.post(
        `/api/v1/organizations/${organization?.organization_id}/coordination/patients/${stage.patient_id}/confirm`,
      ),
    onSuccess: refresh,
  });
  return (
    <Alert>
      <AlertTitle>
        {stage.status === 'approved'
          ? 'Intake approved'
          : stage.status === 'rejected'
            ? 'Intake rejected'
            : 'Provisional intake — approval needed'}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-3">
        <p>
          {stage.created_patient
            ? 'A provisional chart was created with source demographics.'
            : stage.patient_id
              ? 'An existing chart was identified for your review.'
              : 'Patient identity needs review before a chart can be selected or created.'}
        </p>
        <p>
          Review the original fax and any unreadable or conflicting fields below. Saving intake imports the verified
          information; missing information is not inferred.
        </p>
        {stage.status === 'approved' && stage.patient_is_provisional && can('update_patient') && (
          <>
            <p>
              After reviewing all related faxes and chart demographics, confirm the chart to remove provisional status.
            </p>
            <Button
              variant="outline"
              disabled={confirm.isPending || confirm.isSuccess}
              onClick={() => confirm.mutate()}>
              Confirm reviewed chart
            </Button>
            {confirm.isSuccess && <p role="status">Chart confirmed.</p>}
          </>
        )}
        {!['approved', 'rejected'].includes(stage.status) && can('update_patient') && (
          <details>
            <summary>Reject this proposed intake</summary>
            <p>
              The source fax and any provisional chart are retained for follow-up. No clinical information will be
              imported.
            </p>
            <Button variant="outline" disabled={reject.isPending} onClick={() => reject.mutate()}>
              Reject intake
            </Button>
          </details>
        )}
        {(reject.isError || confirm.isError) && (
          <p role="alert">
            {actionError(reject.error || confirm.error, 'Review could not be saved. Refresh and try again.')}
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}
